import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  checkTokenExpiration,
  parseJwtPayload,
} from '../utils/authTokens';
import {
  getOrganizationById,
  getOrganizationOptions,
} from '../hooks/useOrganizations';
import { getCurrentUserInternalRole, getUserRoles } from '../hooks/useUsers';
import {
  acceptEula as acceptEulaRequest,
  changeOrganization as changeOrganizationRequest,
  createAccount as createAccountRequest,
  login as loginRequest,
  refreshToken as refreshTokenRequest,
} from '../hooks/useAuth';
import type {
  LoginInput,
  SessionOrganization,
  SessionUser,
  SignupInput,
} from '../types/index';
import {
  AUTH_STORAGE_KEY,
  REFRESH_STORAGE_KEY,
  ROUTE_EXCLUDE_PREFIXES,
  ROUTE_STORAGE_KEY,
  SELECTED_ORG_ID_STORAGE_KEY,
  getDefaultOrgStorageKey,
} from '../constants';
import { getCookie, setCookie } from '../utils/cookies';
import { eulaVersion } from '../utils/eula';
import {
  checkPermissionActions,
  type PermissionCheckMode,
  permissionSetToRole,
  resolveOrganizationRoleActions,
  resolveRoleActions,
} from '../utils/permissions';
import { internalRoleToPermissionSet } from '../utils/permissionsInternal';
import { getPostAuthenticationRoute } from '../utils/postAuthenticationRoute';
import { showToastError } from '../components/ToastNotifications';
import usePendoLifecycle from '../hooks/usePendoLifecycle';

type LegacyAppSessionValue = {
  authToken: string;
  checkPerms: (actions: string | string[], mode?: PermissionCheckMode) => boolean;
  currentUser: SessionUser | null;
  defaultRoute: string;
  eulaGatePending: boolean;
  handleChangePassword: () => void;
  handleLogin: (payload: LoginInput) => Promise<void>;
  handleLogout: () => void;
  handleSignup: (payload: SignupInput) => Promise<unknown>;
  hasStoredToken: boolean;
  isAuthenticated: boolean;
  isSessionReady: boolean;
  loginBusy: boolean;
  loginError: string;
  org: SessionOrganization | null;
  acceptEula: () => Promise<void>;
  changeOrganization: (org: SessionOrganization) => Promise<void>;
  updateCurrentUserProfile: (user: SessionUser, nextToken?: string) => void;
};

const LegacyAppSessionContext = createContext<LegacyAppSessionValue | null>(null);

function transformObjectKeys(obj) {
  return Object.entries(obj || {}).reduce((acc, [key, value]) => {
    let newKey = key.replace(/Permissions_\d+_/, '');
    newKey = newKey[0].toLowerCase() + newKey.slice(1);
    acc[newKey] = value;
    return acc;
  }, {});
}

const getRoleFromTokenPayload = (payload): string => {
  const payloadWithKeysSimplified = transformObjectKeys(payload);
  return permissionSetToRole(payloadWithKeysSimplified);
};

const isPayloadSuperUser = (payload) => payload?.IsSuperUser === 'True' || payload?.IsSuperUser === true;

const getPayloadDefaultOrganizationId = (payload): number | null => {
  const value = Number(
    payload?.DefaultOrganization ??
      payload?.defaultOrganization ??
      payload?.DefaultOrganizationId ??
      payload?.defaultOrganizationId,
  );
  return Number.isFinite(value) && value > 0 ? value : null;
};

const getExplicitOrganizationId = (payload) =>
  payload?.permissions?.organizationId ||
  payload?.Permissions_0_OrganizationId ||
  payload?.permissions_0_organizationId ||
  null;

const getTokenOrganizationId = (payload) =>
  getExplicitOrganizationId(payload) || getPayloadDefaultOrganizationId(payload);

const getRoleActions = (payload, role: string): string[] => {
  return resolveOrganizationRoleActions(role, payload?.permissions?.actions);
};

const getPermissions = (
  payload,
  role: string,
  organizationId?: SessionOrganization['id'] | null,
) => {
  const actions = getRoleActions(payload, role);
  return {
    organizationId: organizationId ?? getTokenOrganizationId(payload),
    actions,
  };
};

async function getRoleForOrganization(
  organizationId: SessionOrganization['id'] | null | undefined,
  fallbackRole: string,
): Promise<string> {
  if (!organizationId) return fallbackRole;

  try {
    const roles = await getUserRoles(organizationId);
    const resolvedRole = String(roles?.[0] || '').trim().toLowerCase();
    return resolvedRole || fallbackRole;
  } catch {
    return fallbackRole;
  }
}

async function getInternalRoleState(isSuperUser: boolean): Promise<{
  internalRole: string;
  internalRoleActions: string[];
}> {
  if (!isSuperUser) {
    return { internalRole: '', internalRoleActions: [] };
  }

  try {
    const internalRole = await getCurrentUserInternalRole();
    return {
      internalRole,
      internalRoleActions: internalRoleToPermissionSet(internalRole, isSuperUser),
    };
  } catch {
    return { internalRole: '', internalRoleActions: [] };
  }
}

type ProviderProps = {
  children: ReactNode;
};

export function LegacyAppSessionProvider({ children }: ProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(
    () => window.localStorage.getItem(AUTH_STORAGE_KEY) || ''
  );
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [eulaGatePending, setEulaGatePending] = useState(false);
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrganization | null>(null);
  const [isSessionReady, setIsSessionReady] = useState(() => !window.localStorage.getItem(AUTH_STORAGE_KEY));
  const organizationChangeRequestIdRef = useRef(0);
  const loginInFlightRef = useRef(false);
  const { stopGuides: stopPendoGuides } = usePendoLifecycle({
    currentUser,
    isAuthenticated,
    org,
  });

  const hasStoredToken = Boolean(authToken || window.localStorage.getItem(AUTH_STORAGE_KEY));

  const getLocalStorageValue = (key: string) => {
    try {
      return window.localStorage.getItem(key) || '';
    } catch {
      return '';
    }
  };

  const getStoredDefaultOrganizationId = (payload): number | null => {
    const userId = Number(payload?.Id || payload?.id || 0) || 0;
    if (!userId) return null;

    const value = Number(getLocalStorageValue(getDefaultOrgStorageKey(userId)));
    return Number.isFinite(value) && value > 0 ? value : null;
  };

  const parseOrganizationToken = (
    token: string,
    organizationId: SessionOrganization['id'],
  ) => {
    const payload = parseJwtPayload(token);
    if (!payload) {
      throw new Error('The organization change returned an invalid authentication token.');
    }
    const explicitOrganizationId = getExplicitOrganizationId(payload);
    if (
      explicitOrganizationId != null &&
      String(explicitOrganizationId) !== String(organizationId)
    ) {
      throw new Error('The organization change returned a token for a different organization.');
    }
    return payload;
  };

  const loadInitialOrganization = async (payload): Promise<SessionOrganization | null> => {
    const preferredIds = [
      getCookie('selectedOrgId'),
      getLocalStorageValue(SELECTED_ORG_ID_STORAGE_KEY),
      getTokenOrganizationId(payload),
      getStoredDefaultOrganizationId(payload),
    ].filter(
      (id, index, ids) => id && ids.findIndex((candidate) => String(candidate) === String(id)) === index,
    );
    for (const preferredId of preferredIds) {
      try {
        const preferred = await getOrganizationById(preferredId);
        if (preferred) return preferred;
      } catch {
        // Try the next locally preferred ID before falling back.
      }
    }
    const fallback = await getOrganizationOptions({ page: 0, pageSize: 1 });
    return fallback.data[0] || null;
  };

  const defaultRoute = useMemo(() => {
    try {
      if (!authToken) return '/products';
      const saved = window.localStorage.getItem(ROUTE_STORAGE_KEY);
      if (saved && !ROUTE_EXCLUDE_PREFIXES.some((prefix) => saved.startsWith(prefix))) {
        return saved;
      }
    } catch {
      // ignore storage errors
    }
    return '/products';
  }, [authToken]);

  const handleLogout = () => {
    organizationChangeRequestIdRef.current += 1;
    stopPendoGuides();
    setIsAuthenticated(false);
    setAuthToken('');
    setLoginError('');
    setEulaGatePending(false);
    setCurrentUser(null);
    setOrg(null);
    setIsSessionReady(true);

    try {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
      window.localStorage.removeItem(REFRESH_STORAGE_KEY);
      window.localStorage.removeItem(ROUTE_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }

    navigate('/login' + window.location.search);
  };

  const handleLogin = async ({ email, password }: LoginInput) => {
    if (loginInFlightRef.current) return;

    loginInFlightRef.current = true;
    setLoginError('');
    setLoginBusy(true);
    let sessionCommitted = false;
    try {
      const response = await loginRequest({ email, password });
      const data = response.data;
      const refreshToken = response.refreshToken;

      if (!data?.token) {
        setLoginError('Sign in failed');
        return;
      }

      sessionCommitted = true;
      window.localStorage.setItem(AUTH_STORAGE_KEY, data.token);
      setIsSessionReady(false);

      if (refreshToken) {
        window.localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
      }

      const payload = parseJwtPayload(data.token);
      if (!payload) {
        throw new Error('Sign in returned an invalid authentication token.');
      }
      const selectedOrg = await loadInitialOrganization(payload);
      let sessionToken = data.token;
      let sessionPayload = payload;
      if (
        selectedOrg?.id &&
        String(selectedOrg.id) !== String(getTokenOrganizationId(payload) || '')
      ) {
        const changedSession = await changeOrganizationRequest(selectedOrg.id);
        if (!changedSession?.token) {
          throw new Error('The organization change did not return an authentication token.');
        }
        const changedPayload = parseOrganizationToken(changedSession.token, selectedOrg.id);
        sessionToken = changedSession.token;
        sessionPayload = changedPayload;
        window.localStorage.setItem(AUTH_STORAGE_KEY, sessionToken);
      }

      const tokenRole = getRoleFromTokenPayload(sessionPayload) || 'viewer';
      const isSuperUser = isPayloadSuperUser(sessionPayload);
      const internalRoleState = await getInternalRoleState(isSuperUser);
      const resolvedRole = selectedOrg?.id
        ? await getRoleForOrganization(selectedOrg.id, tokenRole)
        : tokenRole;
      const resolvedPermissions = getPermissions(sessionPayload, resolvedRole, selectedOrg?.id);

      setAuthToken(sessionToken);
      setOrg(selectedOrg);
      setCurrentUser({
        id: Number(sessionPayload.Id || sessionPayload.id || 0) || 0,
        name: sessionPayload.Name || sessionPayload.name || 'User',
        email: sessionPayload.Email || sessionPayload.email || '',
        role: resolvedRole,
        org: '',
        isSuperUser,
        ...internalRoleState,
        permissions: resolvedPermissions,
        roleActions: resolveRoleActions({
          role: resolvedRole,
          organizationActions: resolvedPermissions.actions,
          internalRole: internalRoleState.internalRole,
        }),
        defaultOrganizationId:
          getPayloadDefaultOrganizationId(payload) ?? getStoredDefaultOrganizationId(payload),
      });
      setIsAuthenticated(true);

      const agreedVersion = sessionPayload?.EulaVersion || sessionPayload?.eulaVersion || '';
      if (agreedVersion < eulaVersion) {
        setEulaGatePending(true);
        setIsSessionReady(true);
        return;
      }

      navigate(getPostAuthenticationRoute(locationRef.current));
    } catch (error) {
      if (sessionCommitted) {
        setAuthToken('');
        setIsAuthenticated(false);
        setCurrentUser(null);
        setOrg(null);
        setEulaGatePending(false);
        try {
          window.localStorage.removeItem(AUTH_STORAGE_KEY);
          window.localStorage.removeItem(REFRESH_STORAGE_KEY);
        } catch {
          // ignore storage errors
        }
      }
      if (error?.status === 401) setLoginError('Invalid email or password');
      else if (error?.status === 403) setLoginError('Email not verified');
      else setLoginError('Could not reach backend');
    } finally {
      loginInFlightRef.current = false;
      setLoginBusy(false);
      setIsSessionReady(true);
    }
  };

  const handleSignup = async ({
    name,
    email,
    password,
    organizationName,
    eulaVersion: agreedVersion,
  }: SignupInput) => createAccountRequest({
    name,
    email,
    password,
    organizationName,
    eulaVersion: agreedVersion,
  });

  const changeOrganization = useCallback(async (nextOrg: SessionOrganization) => {
    const requestId = ++organizationChangeRequestIdRef.current;
    const data = await changeOrganizationRequest(nextOrg.id);
    if (!data?.token) {
      throw new Error('The organization change did not return an authentication token.');
    }
    const payload = parseOrganizationToken(data.token, nextOrg.id);
    const role = getRoleFromTokenPayload(payload);
    const isSuperUser = isPayloadSuperUser(payload);
    const internalRoleState = await getInternalRoleState(isSuperUser);
    const resolvedRole = await getRoleForOrganization(nextOrg.id, role || 'viewer');
    if (requestId !== organizationChangeRequestIdRef.current) return;
    const resolvedPermissions = getPermissions(payload, resolvedRole, nextOrg.id);
    const previousUser = currentUser;
    window.localStorage.setItem(AUTH_STORAGE_KEY, data.token);
    setAuthToken(data.token);
    setCurrentUser({
      id: Number(payload.Id || payload.id || 0) || 0,
      name: payload.Name || payload.name || 'User',
      email: payload.Email || payload.email || '',
      role: resolvedRole,
      org: previousUser?.org || '',
      isSuperUser,
      ...internalRoleState,
      permissions: resolvedPermissions,
      roleActions: resolveRoleActions({
        role: resolvedRole,
        organizationActions: resolvedPermissions.actions,
        internalRole: internalRoleState.internalRole,
      }),
      defaultOrganizationId:
        getPayloadDefaultOrganizationId(payload) ?? previousUser?.defaultOrganizationId ?? null,
    });
    setOrg(nextOrg);
  }, [currentUser]);

  const handleChangePassword = () => {
    setIsAuthenticated(false);
    navigate('/forgot');
  };

  const acceptEula = async () => {
    try {
      const currentToken = window.localStorage.getItem(AUTH_STORAGE_KEY);
      const payload = currentToken ? parseJwtPayload(currentToken) : null;
      const orgId =
        org?.id ||
        getTokenOrganizationId(payload);
      const accepted = await acceptEulaRequest(eulaVersion, orgId);

      if (accepted?.refreshToken) {
        window.localStorage.setItem(REFRESH_STORAGE_KEY, accepted.refreshToken);
      }

      let nextToken = accepted?.data?.token;
      if (!nextToken && orgId) {
        const refreshed = await refreshTokenRequest(orgId);
        nextToken = refreshed?.token;
      }

      if (!nextToken) {
        throw new Error('No updated session token was returned after accepting the EULA.');
      }

      const updatedPayload = parseJwtPayload(nextToken);
      const agreedVersion = updatedPayload?.EulaVersion || updatedPayload?.eulaVersion || '';
      if (agreedVersion < eulaVersion) {
        throw new Error('The updated session token did not include the accepted EULA version.');
      }

      window.localStorage.setItem(AUTH_STORAGE_KEY, nextToken);
      setAuthToken(nextToken);
      setEulaGatePending(false);
      navigate(getPostAuthenticationRoute(locationRef.current));
    } catch (error) {
      console.error('EULA accept flow failed:', error);
      showToastError('Could not accept the user license agreement. Please try again.');
      throw error;
    }
  };

  const updateCurrentUserProfile = (updatedUser: SessionUser, nextToken?: string) => {
    if (nextToken) {
      setAuthToken(nextToken);
      try {
        window.localStorage.setItem(AUTH_STORAGE_KEY, nextToken);
      } catch {
        // ignore storage errors
      }
    }
    setCurrentUser(updatedUser);
    try {
      if (updatedUser?.id) {
        const storageKey = getDefaultOrgStorageKey(updatedUser.id);
        if (updatedUser.defaultOrganizationId) {
          window.localStorage.setItem(storageKey, String(updatedUser.defaultOrganizationId));
        } else {
          window.localStorage.removeItem(storageKey);
        }
      }
    } catch {
      // ignore storage errors
    }
  };

  useEffect(() => {
    if (!org?.id) return;
    setCookie('selectedOrgId', org.id);
    try {
      window.localStorage.setItem(SELECTED_ORG_ID_STORAGE_KEY, String(org.id));
    } catch {
      // ignore storage errors
    }
  }, [org]);

  useEffect(() => {
    try {
      if (!isAuthenticated) return;
      const path = (location.pathname || '') + (location.search || '');
      if (!ROUTE_EXCLUDE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) {
        window.localStorage.setItem(ROUTE_STORAGE_KEY, path);
      }
    } catch {
      // ignore storage errors
    }
  }, [location.pathname, location.search, isAuthenticated]);

  useEffect(() => {
    if (!authToken) {
      setIsSessionReady(true);
      return;
    }

    const payload = parseJwtPayload(authToken);
    const isSuperUser = isPayloadSuperUser(payload);
    const tokenRole = payload ? getRoleFromTokenPayload(payload) : 'viewer';
    const initialPermissions = payload ? getPermissions(payload, tokenRole || 'viewer') : undefined;

    if (payload) {
      setCurrentUser({
        id: Number(payload.Id || payload.id || 0) || 0,
        name: payload.Name || payload.name || 'User',
        email: payload.Email || payload.email || '',
        role: tokenRole || payload.role || 'viewer',
        org: '',
        isSuperUser,
        permissions: initialPermissions,
        roleActions: initialPermissions?.actions || [],
        defaultOrganizationId:
          getPayloadDefaultOrganizationId(payload) ?? getStoredDefaultOrganizationId(payload),
      });
      setIsAuthenticated(true);

      const agreedVersion = payload?.EulaVersion || payload?.eulaVersion || '';
      if (agreedVersion < eulaVersion) {
        setEulaGatePending(true);
      }
    }

    loadInitialOrganization(payload)
      .then(async (selectedOrg) => {
        const uiOrg = selectedOrg;
        let resolvedPayload = payload;
        if (
          uiOrg?.id &&
          String(uiOrg.id) !== String(getTokenOrganizationId(payload) || '')
        ) {
          const changedSession = await changeOrganizationRequest(uiOrg.id);
          if (!changedSession?.token) {
            throw new Error('The organization change did not return an authentication token.');
          }
          resolvedPayload = parseOrganizationToken(changedSession.token, uiOrg.id);
          window.localStorage.setItem(AUTH_STORAGE_KEY, changedSession.token);
          setAuthToken(changedSession.token);
        }
        const resolvedIsSuperUser = isPayloadSuperUser(resolvedPayload);
        const internalRoleState = await getInternalRoleState(resolvedIsSuperUser);
        let resolvedRole = getRoleFromTokenPayload(resolvedPayload) || tokenRole || 'viewer';
        if (uiOrg?.id) {
          resolvedRole = await getRoleForOrganization(uiOrg.id, resolvedRole);
        }
        const resolvedPermissions = getPermissions(resolvedPayload, resolvedRole, uiOrg?.id);
        setCurrentUser((prev) => (prev ? {
          ...prev,
          isSuperUser: resolvedIsSuperUser,
          ...internalRoleState,
          role: resolvedRole,
          permissions: resolvedPermissions,
          roleActions: resolveRoleActions({
            role: resolvedRole,
            organizationActions: resolvedPermissions.actions,
            internalRole: internalRoleState.internalRole,
          }),
        } : prev));
        setOrg(uiOrg);

        try {
          const saved = window.localStorage.getItem(ROUTE_STORAGE_KEY);
          const currentPath = window.location.pathname || '';
          const shouldRestoreSavedRoute = currentPath === '/' || currentPath === '';
          if (
            saved &&
            shouldRestoreSavedRoute &&
            !ROUTE_EXCLUDE_PREFIXES.some((prefix) => saved.startsWith(prefix)) &&
            !ROUTE_EXCLUDE_PREFIXES.some((prefix) => currentPath.startsWith(prefix))
          ) {
            navigate(saved, { replace: true });
          }
        } catch {
          // ignore route restore errors
        }
      })
      .catch(() => {
        handleLogout();
      })
      .finally(() => {
        setIsSessionReady(true);
      });
     
  }, []);

  useEffect(() => {
    const handleTokenRefreshed = (event: CustomEvent) => {
      const newToken = event.detail?.token;
      if (newToken) {
        setAuthToken(newToken);
      }
    };

    window.addEventListener('tokenRefreshed', handleTokenRefreshed as EventListener);
    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefreshed as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !authToken) return;

    const currentToken = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!currentToken) return;

    const payload = parseJwtPayload(currentToken);

    const tokenStatus = checkTokenExpiration(currentToken);
    if (!tokenStatus.isExpiringSoon && !tokenStatus.isExpired) return;

    const orgId = org?.id || getExplicitOrganizationId(payload);

    if (!orgId) return;

    refreshTokenRequest(orgId)
      .then((data) => {
        if (!data?.token) return;
        window.localStorage.setItem(AUTH_STORAGE_KEY, data.token);
        setAuthToken(data.token);
      })
      .catch(() => {
        // allow the next API request to surface 401 naturally
      });
  }, [authToken, isAuthenticated, location.pathname, org?.id]);

  const value = useMemo(
    () => ({
      authToken,
      checkPerms: (actions: string | string[], mode: PermissionCheckMode = 'any') =>
        checkPermissionActions(
          Array.from(new Set([
            ...(currentUser?.permissions?.actions || []),
            ...(currentUser?.roleActions || []),
            ...(currentUser?.internalRoleActions || []),
          ].filter(Boolean))),
          actions,
          mode,
        ),
      currentUser,
      defaultRoute,
      eulaGatePending,
      handleChangePassword,
      handleLogin,
      handleLogout,
      handleSignup,
      hasStoredToken,
      isAuthenticated,
      isSessionReady,
      loginBusy,
      loginError,
      org,
      acceptEula,
      changeOrganization,
      updateCurrentUserProfile,
    }),
    [
      authToken,
      currentUser,
      defaultRoute,
      eulaGatePending,
      hasStoredToken,
      isAuthenticated,
      isSessionReady,
      loginBusy,
      loginError,
      org,
    ]
  );

  return (
    <LegacyAppSessionContext.Provider value={value}>
      {children}
    </LegacyAppSessionContext.Provider>
  );
}

export function useLegacyAppSession() {
  const context = useContext(LegacyAppSessionContext);
  if (!context) {
    throw new Error('useLegacyAppSession must be used within LegacyAppSessionProvider');
  }
  return context;
}
